import {
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
} from '@tanstack/react-table';

import { DATA_TABLE_ROW_EXPANDED_ID_SUFFIX } from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { TableMemoTable } from '../types/table.types.js';

/**
 * Row model which reflects the order given by the rowOrder set.
 * @returns new row model reflecting the order.
 * @internal
 */
export function createRowOrderRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'DataTableRowOrderFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getRowOrderRowModel',
      memoDeps: () => [
        table.getPreRowOrderRowModel(),
        table.options.enableRowOrdering,
        table.atoms.rowOrder?.get(),
        table.atoms.sorting?.get(),
      ],
      fn: (preRowModel) => _createRowOrderRowModel(table),
    });
  };
}

function _createRowOrderRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(table: Table<TDataTableFeatures, TData>) {
  const preRowOrderRowModel = table.getPreRowOrderRowModel();
  const rowOrder = table.atoms.rowOrder?.get();
  const storingState = table.atoms.sorting?.get();

  // only order if rowOrder is enabled and we are not currently sorting
  if (
    table.options.enableRowOrdering !== true ||
    (storingState && storingState.length !== 0)
  ) {
    return preRowOrderRowModel;
  }

  const { flatRows, rows, rowsById } = preRowOrderRowModel;

  if (rowOrder === undefined) {
    table.setRowOrder(flatRows.map((row) => row.id));
    return preRowOrderRowModel;
  }

  const orderRows = (rows: Row<TDataTableFeatures, TData>[]) => {
    const orderedData = [...rows];

    orderedData.sort((rowA, rowB) => rowOrderCompareFn(rowOrder, rowA, rowB));

    // If there are sub-rows, also order them
    orderedData.forEach((row) => {
      if (row.subRows?.length) {
        row.subRows = orderRows(row.subRows);
      }
    });

    return orderedData;
  };

  const newRowsById = Object.keys(rowsById)
    .sort((a, b) => rowOrderCompareStrFn(rowOrder, a, b))
    .reduce(
      (obj: Record<string, Row<TDataTableFeatures, TData>>, key: string) => {
        obj[key] = rowsById[key];
        return obj;
      },
      {},
    );

  return {
    flatRows: orderRows(flatRows),
    rows: orderRows(rows),
    rowsById: newRowsById,
  };
}

/**
 * Compares when a and b are objects.
 * @param rowOrder - set of ids.
 * @param a - object containing id 1
 * @param b - object containing id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareFn(
  rowOrder: string[],
  a: { id: string },
  b: { id: string },
): number {
  let indexA = rowOrder.indexOf(a.id);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b.id);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}

/**
 * Compares when a and b are strings.
 * @param rowOrder - set of ids.
 * @param a - id 1
 * @param b - id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareStrFn(
  rowOrder: string[],
  a: string,
  b: string,
): number {
  let indexA = rowOrder.indexOf(a);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}
